import { useLocalization } from '@/hooks/use-localization';
import { AllContactMeFormsProps } from '@/isomorphic/components/wp-blocks/contact-form/contact-form';
import { FieldProps } from '@/isomorphic/components/forms/components/field';

export function useForm() {
    const { localize } = useLocalization();

    const formatName = (name: string) => {
        let translatedName = localize(name);

        if (name === 'newsletter') {
            translatedName = localize('newsletter-sign-up-phrase');
        } else if (name === 'optIn') {
            translatedName = localize('opt-in');
        }
        return `${translatedName.charAt(0).toUpperCase()}${translatedName.substring(1)}`;
    };

    // Since our current WP block does not support the ability to rearrange fields, we need to do it here. It's a quickfix, but it works.
    const rearrangeFields = (enabledFields: string[]) => {
        // Separate checkbox fields and message field (always rendered last, just before checkboxes)
        const checkboxFields = ['optIn', 'newsletter'];
        const trailingFields = ['message'];

        const nonSpecialFields = enabledFields.filter(field => !checkboxFields.includes(field) && !trailingFields.includes(field));
        const messageFields = enabledFields.filter(field => trailingFields.includes(field));
        const fieldsWithCheckboxes = enabledFields.filter(field => checkboxFields.includes(field));

        // move socialSecurityNumber to the top
        const ssnIndex = nonSpecialFields.indexOf('socialSecurityNumber');
        if (ssnIndex >= 0) {
            nonSpecialFields.unshift(nonSpecialFields.splice(ssnIndex, 1)[0]);
        }

        // then move products to the top...
        const productsIndex = nonSpecialFields.indexOf('products');
        if (productsIndex >= 0) {
            nonSpecialFields.unshift(nonSpecialFields.splice(productsIndex, 1)[0]);
        }

        // if accountNumberIndex is higher than genericTextIndex, set genericTextIndex one higher than accountNumberIndex
        const accountNumberIndex = nonSpecialFields.indexOf('accountNumber');
        const genericTextIndex = nonSpecialFields.indexOf('genericText');

        if (accountNumberIndex >= 0 && genericTextIndex >= 0 && accountNumberIndex > genericTextIndex) {
            nonSpecialFields.splice(genericTextIndex, 0, nonSpecialFields.splice(accountNumberIndex, 1)[0]);
        }

        // Append message fields, then checkbox fields at the very end
        return [...nonSpecialFields, ...messageFields, ...fieldsWithCheckboxes];
    };

    const parsePropsToFieldObjects = (props: AllContactMeFormsProps) => {
        const enabledFields = Object.keys(props)
            .filter(prop => prop.includes('Enable'))
            .filter(key => props[key as keyof typeof props])
            .map(key => key.split('Enable')[0]);

        return rearrangeFields(enabledFields).map(field => {
            const objects = Object.fromEntries(
                Object.keys(props)
                    .filter(key => key.includes(field))
                    .map(key => {
                        const newKey = key.split(field)[1];
                        const formattedKey = `${newKey.charAt(0).toLowerCase()}${newKey.substring(1)}`;
                        return [formattedKey, props[key as keyof typeof props]];
                    })
            );

            const isRequired = props[`${field}Required` as keyof typeof props] === true;
            let label = localize(field);
            let placeholder = '';

            let type = 'input';
            let dropText = '';
            let list: { name: string; value: string; notAllowed?: boolean }[] | undefined;
            let fieldNameOverride: string | undefined;
            switch (field) {
                case 'genericText':
                    type = 'input';
                    if (props[`${field}Label`]) {
                        label = props[`${field}Label`];
                    }
                    break;
                case 'optIn':
                    type = 'checkbox';
                    label = formatName(field);
                    break;
                case 'customCheckbox':
                    type = 'checkbox';
                    label = props[`${field}Label`];
                    break;
                case 'newsletter':
                    type = 'checkbox';
                    label = formatName(field);
                    break;
                case 'products':
                    type = 'select';
                    if (props[`${field}Label` as keyof typeof props] === true) {
                        label = props[`${field}Label`];
                    }
                    if (props[`${field}DefaultSelectionText`]) {
                        placeholder = props[`${field}DefaultSelectionText`];
                    }
                    break;
                case 'dropdown': {
                    type = 'select';
                    // Label must be set by admin - also use it as field name for email
                    const dropdownLabel = props['dropdownLabel' as keyof typeof props] as string;
                    if (dropdownLabel) {
                        label = dropdownLabel;
                        fieldNameOverride = dropdownLabel;
                    }
                    // Parse CSV values - values ending with * are "not allowed" (for B2B conditional logic)
                    const csv = props['dropdownCsv' as keyof typeof props] as string;
                    if (csv) {
                        list = csv.split(',').map(item => {
                            const trimmed = item.trim();
                            const notAllowed = trimmed.endsWith('*');
                            const name = notAllowed ? trimmed.slice(0, -1) : trimmed;
                            return { name, value: name, notAllowed };
                        });
                    }
                    break;
                }
                case 'message':
                    type = 'textarea';
                    break;
                case 'annualRevenue':
                    type = 'input';
                    break;
                case 'loanAmount':
                    type = 'input';
                    break;
                case 'files':
                    type = 'file';
                    if (props[`${field}DropText`]) {
                        dropText = props[`${field}DropText`];
                    }
                    break;
                case 'phone':
                    type = 'input';
            }

            return {
                fieldName: fieldNameOverride || field,
                dropText,
                placeholder,
                type,
                label,
                required: isRequired,
                ...(list && { list }),
                ...objects,
            } as FieldProps & { dropText: string };
        });
    };

    return {
        parsePropsToFieldObjects,
    };
}
